/**
 * Karbon360° v2 — Data quality.
 *
 * What an assurance reviewer will ask about, answered from live data.
 *
 * DELIBERATELY NOT a single composite "quality score". The prototype showed one,
 * but any weighting we invented would be exactly the kind of unverifiable claim
 * this product exists to prevent. Instead each dimension is reported separately
 * and each is independently defensible:
 *   Verified   — share of tCO₂e whose stored figure reconciles with its factor
 *   Evidenced  — share of tCO₂e with a source document attached
 *   Complete   — site-months with data
 *   Findings   — things a reviewer would raise
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ErrorBoundary from '../components/ErrorBoundary';
import { Shell } from '../components/Shell';
import { X } from '../components/v2/Icons';
import {
  PageHeader, Card, Button, Stat, Badge, Callout, Empty, Skeleton,
  BarRow, DataQuality, fmt,
} from '../components/v2/ui';
import { Heatmap } from '../components/v2/ui/Charts';
import { useT } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const num = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v));
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

function DataQualityScreen() {
  const { t } = useT();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const y = new Date().getFullYear();
    Promise.all([
      axios.get(`${API_URL}/api/invoices`, auth()).then((r) => r.data),
      axios.get(`${API_URL}/api/emissions/coverage/matrix?year=${y}`, auth()).then((r) => r.data).catch(() => null),
    ])
      .then(([recs, m]) => {
        if (!alive) return;
        setRows((Array.isArray(recs) ? recs : []).filter((r) => r.type === 'emission_record'));
        setMatrix(m);
      })
      .catch((e) => { if (alive) setError(e.response?.data?.message || 'Could not load data quality.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const stats = useMemo(() => {
    const total = rows.reduce((s, r) => s + num(r.emissions_tco2e), 0);
    const verified = rows.filter((r) => r.calc_check === 'match');
    const mismatched = rows.filter((r) => r.calc_check === 'mismatch');
    const unverified = rows.filter((r) => !r.calc_check);
    const evidenced = rows.filter((r) => r.invoice_id || r.source === 'ocr');
    const sum = (a) => a.reduce((s, r) => s + num(r.emissions_tco2e), 0);

    return {
      total,
      verifiedT: sum(verified), verifiedN: verified.length,
      mismatchT: sum(mismatched), mismatchN: mismatched.length,
      unverifiedT: sum(unverified), unverifiedN: unverified.length,
      evidencedT: sum(evidenced), evidencedN: evidenced.length,
      mismatched,
    };
  }, [rows]);

  /**
   * Group the mismatches by the factor their stored figure IMPLIES. This turns
   * a list of 22 rows into two or three actionable findings, and names the
   * probable cause instead of leaving the reviewer to infer it.
   */
  const mismatchGroups = useMemo(() => {
    const g = new Map();
    stats.mismatched.forEach((r) => {
      const onFile = num(r.factor_value);
      const server = num(r.server_tco2e);
      if (!onFile || !server) return;

      // Implied factor = onFile x (stored / server). Deriving it from the raw
      // quantity would compare a per-kg figure against a per-tonne factor for
      // process records — the calculator converts units before applying the
      // factor, so the raw quantity is not in the factor's unit. This form is
      // unit-agnostic by construction.
      const implied = Math.round((onFile * (num(r.emissions_tco2e) / server)) * 10000) / 10000;
      const key = `${r.factor_key || 'unknown'}|${implied}`;
      if (!g.has(key)) {
        g.set(key, { factorKey: r.factor_key, implied, onFile, records: [], stored: 0, correct: 0 });
      }
      const e = g.get(key);
      e.records.push(r);
      e.stored += num(r.emissions_tco2e);
      e.correct += num(r.server_tco2e);
    });
    return [...g.values()].sort((a, b) => Math.abs(b.correct - b.stored) - Math.abs(a.correct - a.stored));
  }, [stats.mismatched]);

  const siteCoverage = useMemo(() => {
    if (!matrix?.sites) return [];
    return matrix.sites
      .map((s) => ({ name: s.name, covered: s.covered, pctv: Math.round((s.covered / 12) * 100) }))
      .sort((a, b) => b.pctv - a.pctv);
  }, [matrix]);

  if (loading) {
    return (
      <div className="k2-wrap">
        <PageHeader title={t('dq.title')} desc={t('dq.loading')} />
        <div className="k2-g4" style={{ marginBottom: 24 }}>
          {[0, 1, 2, 3].map((i) => <Card key={i}><Skeleton h={54} /></Card>)}
        </div>
      </div>
    );
  }
  if (error) {
    return <div className="k2-wrap"><PageHeader title={t('dq.title')} /><Callout tone="err">{error}</Callout></div>;
  }

  const s = stats;
  const findings = [];

  mismatchGroups.forEach((g) => {
    const delta = g.correct - g.stored;
    const many = g.records.length > 1;
    findings.push({
      tone: Math.abs(delta) > 1 ? 'e' : 'w',
      icon: Math.abs(delta) > 1 ? 'Alert' : 'Warn',
      title: `${g.records.length} record${many ? 's' : ''} ${many ? 'imply' : 'implies'} a factor of ${g.implied} — the library holds ${g.onFile || '—'}`,
      body: `${fmt(g.stored, 3)} tCO₂e filed where ${fmt(g.correct, 3)} is produced by the factor on file` +
            (Math.abs(delta) > 1 ? ` · understated by ${fmt(Math.abs(delta), 2)} tCO₂e` : ' · immaterial'),
      badge: String(g.records.length),
      to: '/records',
    });
  });

  if (s.unverifiedN > 0) {
    findings.push({
      tone: 'i', icon: 'Info',
      title: `${s.unverifiedN} record${s.unverifiedN > 1 ? 's have' : ' has'} no calculation trace`,
      body: 'Recorded before server-side verification; the factor applied was not captured',
      badge: String(s.unverifiedN), to: '/records',
    });
  }

  const noEvidence = rows.length - s.evidencedN;
  if (noEvidence > 0) {
    findings.push({
      tone: 'w', icon: 'Warn',
      title: `${noEvidence} record${noEvidence > 1 ? 's have' : ' has'} no source document attached`,
      body: 'Assurance normally requires an invoice or meter reading behind each figure',
      badge: String(noEvidence), to: '/records',
    });
  }

  if (matrix?.summary && matrix.summary.covered < matrix.summary.siteMonths) {
    const gap = matrix.summary.siteMonths - matrix.summary.covered;
    findings.push({
      tone: 'w', icon: 'Warn',
      title: `${gap} site-month${gap > 1 ? 's have' : ' has'} no data in ${matrix.year}`,
      body: `${matrix.summary.covered} of ${matrix.summary.siteMonths} covered — gaps block assurance`,
      badge: String(gap), to: '/regjistrimi',
    });
  }

  if (matrix?.unassignedRecords > 0) {
    findings.push({
      tone: 'w', icon: 'Warn',
      title: `${matrix.unassignedRecords} record${matrix.unassignedRecords > 1 ? 's are' : ' is'} not linked to a facility`,
      body: 'Excluded from per-site intensity and from the coverage grid',
      badge: String(matrix.unassignedRecords), to: '/records',
    });
  }

  findings.push({
    tone: 'i', icon: 'Info',
    title: 'Scope 3 covers 2 of 15 GHG Protocol categories',
    body: 'Business travel and employee commuting only — the boundary is narrow and should be stated in the report',
    to: '/scope-3',
  });

  const blocking = findings.filter((f) => f.tone === 'e').length;

  return (
    <div className="k2-wrap">
      <PageHeader
        title={t('dq.title')}
        desc="What an assurance reviewer will ask about. Each dimension is reported separately — there is no single composite score, because any weighting would be a claim we could not defend."
      >
        <Button icon={X.Doc} onClick={() => navigate('/records')}>Open records</Button>
      </PageHeader>

      <div className="k2-g4" style={{ marginBottom: 24 }}>
        <Stat
          tone="dark"
          label={t('dq.verified')}
          value={pct(s.verifiedT, s.total)}
          unit="% of tCO₂e"
          foot={`${s.verifiedN} of ${rows.length} records reconcile`}
        />
        <Stat
          label={t('dq.evidenced')}
          value={pct(s.evidencedT, s.total)}
          unit="% of tCO₂e"
          foot={`${s.evidencedN} records have a source document`}
        />
        <Stat
          label={t('dq.completeness')}
          value={matrix?.summary ? matrix.summary.coveragePct : '—'}
          unit={matrix?.summary ? '%' : ''}
          foot={matrix?.summary ? `${matrix.summary.covered} of ${matrix.summary.siteMonths} site-months` : ''}
        />
        <Stat
          tone={blocking ? undefined : 'mint'}
          label={t('dq.findings')}
          value={findings.length}
          foot={blocking ? `${blocking} material` : 'none material'}
        />
      </div>

      <div className="k2-g64" style={{ marginBottom: 24 }}>
        <Card
          title={t('dq.findingsTitle')}
          sub={t('dq.findingsSub')}
          right={blocking > 0 ? <Badge tone="err" dot>{blocking} material</Badge> : <Badge tone="ok">None material</Badge>}
          bodyProps={{ style: { padding: '4px 0 0' } }}
        >
          {findings.length === 0 ? (
            <Empty icon={X.Check} title={t('dq.nothing')} body={t('dq.nothingBody')} />
          ) : findings.map((f, i) => {
            const Ic = X[f.icon] || X.Info;
            return (
              <div className="k2-task" key={i} onClick={() => navigate(f.to)}>
                <span className={`k2-ic k2-${f.tone}`}><Ic s={15} /></span>
                <span className="k2-tx">
                  <span className="k2-a">{f.title}</span>
                  <span className="k2-b2">{f.body}</span>
                </span>
                {f.badge && <Badge tone={f.tone === 'e' ? 'err' : 'warn'}>{f.badge}</Badge>}
                <span className="k2-go"><X.Chev s={15} /></span>
              </div>
            );
          })}
        </Card>

        <div className="k2-gcol">
          <Card title={t('dq.evidenceBasis')} sub={t('dq.evidenceBasisSub')}>
            <BarRow
              name={<DataQuality q={3} />}
              value={fmt(s.evidencedT, 2)} unit="t"
              pct={pct(s.evidencedT, s.total)} color="var(--ok)"
            />
            <BarRow
              name={<DataQuality q={2} />}
              value={fmt(s.verifiedT, 2)} unit="t"
              pct={pct(s.verifiedT, s.total)} color="var(--brand-700)"
            />
            <BarRow
              name={<DataQuality q={1} />}
              value={fmt(s.mismatchT + s.unverifiedT, 2)} unit="t"
              pct={pct(s.mismatchT + s.unverifiedT, s.total)} color="var(--warn)"
            />
            <div style={{ marginTop: 12 }}>
              <Callout tone={pct(s.verifiedT, s.total) >= 90 ? 'ok' : 'warn'}>
                Assurance typically expects ≥90% of the footprint to be verifiable.
                You are at <b>{pct(s.verifiedT, s.total)}%</b>.
              </Callout>
            </div>
          </Card>

          <Card title={t('dq.coverageFacility')} sub={`Months with data in ${matrix?.year ?? ''}`}>
            {siteCoverage.length === 0
              ? <Empty icon={X.Building} title={t('dq.noFacilities')} body={t('dq.noFacilitiesBody')} />
              : siteCoverage.map((x) => (
                <BarRow
                  key={x.name}
                  name={x.name}
                  value={String(x.pctv)} unit="%"
                  pct={x.pctv}
                  color={x.pctv > 80 ? 'var(--ok)' : x.pctv > 40 ? 'var(--brand-700)' : 'var(--warn)'}
                />
              ))}
          </Card>
        </div>
      </div>

      <Card title={t('dq.coverageMatrix')} sub={`Facility × month in ${matrix?.year ?? ''}`}>
        <Heatmap sites={matrix?.sites || []} onCell={(site, m) => navigate(m.state === 'none' ? '/regjistrimi' : '/records')} />
      </Card>
    </div>
  );
}

export default function DataQualityPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <DataQualityScreen />
      </Shell>
    </ErrorBoundary>
  );
}
